import dateFormat from "dateformat";
import isEmpty from "is-empty";
import moment, { Moment } from "moment";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { updatePartnerAccountMutation } from "rsl-api-106/lib/mutations";
import {
  CriteriaValueInput,
  Maybe,
  Mutation,
  MutationUpdatePartnerAccountArgs,
  Service,
} from "rsl-api-106/lib/types";

import { useApolloClient } from "@apollo/client";
import Button from "@components/_Layout/Controls/Button";
import InputPassword from "@components/_Layout/Controls/InputPassword";
import InputPasswordConfirm from "@components/_Layout/Controls/InputPasswordConfirm";
import InputText from "@components/_Layout/Controls/InputText";
import RadioCivility from "@components/_Layout/Controls/RadioCivility";
import SDPicker from "@components/_Layout/Controls/SDPicker";
import SelectCivility from "@components/_Layout/Controls/SelectCivility";
import CustomerCriterias from "@components/Criterias/CustomerCriterias";
import { useAuth, useSession } from "@hooks";
import { passwordMinLength } from "@utils/validators";

type CustomerDetailsProps = {
  user?: Service | null;
  showDeleteAccount?: boolean;
};

const languagesVarsCodes = [
  "LANG_neobooking_updating_account_in_progress", //Mise à jour de votre profil en cours
  "LANG_neobooking_account_successfully_updated", //Votre compte a bien été modifié !
  "LANG_neobooking_civility", //Civilité
  "LANG_neobooking_lastname", //Nom
  "LANG_neobooking_firstname", //Prénom
  "LANG_neobooking_city", //Ville
  "LANG_neobooking_adr_street", //N° et voie
  "LANG_neobooking_adr_zipcode", //Code postal
  "LANG_neobooking_adr_tel_1", //Téléphone 1
  "LANG_neobooking_confirm_my_information", //Valider mes modifications
  "LANG_neobooking_editing", //Modification
];

const CustomerDetails: React.FC<CustomerDetailsProps> = ({
  user,
  showDeleteAccount = true,
}) => {
  /* Hooks */
  const { getLanguageVarValue } = useSession();
  const { update } = useAuth();

  /* Loading State */
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  /* User State */
  const [email, setEmail] = useState<Maybe<string> | undefined>(
    user?.address?.mail
  );
  const [username, setUsername] = useState<Maybe<string> | undefined>(
    user?.login
  );
  const [password, setPassword] = useState<string>("*********");
  const [passwordConfirm, setPasswordConfirm] = useState<string>("*********");

  const [lastname, setLastname] = useState<Maybe<string> | undefined>(
    user?.lastName ?? ""
  );
  const [firstname, setFirstname] = useState<Maybe<string> | undefined>(
    user?.firstName ?? ""
  );

  const [birthdate, setBirthdate] = useState<Moment | null>(
    user?.birthDate ? moment(new Date(user.birthDate)) : null
  );
  const [language, setLanguage] = useState<Maybe<string> | undefined>(
    user?.serviceLanguage?.code
  );
  const [civility, setCivility] = useState<Maybe<string> | undefined>(
    user?.civility?.code
  );
  const [country, setCountry] = useState<Maybe<string> | undefined>(
    user?.address?.addressCountry?.code
  );

  const [streetName, setStreetName] = useState<Maybe<string> | undefined>(
    user?.address?.streetName
  );
  const [postalLocality, setPostalLocality] = useState<
    Maybe<string> | undefined
  >(user?.address?.postalLocality);
  const [addressComplementary1, setAddressComplementary1] = useState<
    Maybe<string> | undefined
  >(user?.address?.addressComplementary1);
  const [addressComplementary2, setAddressComplementary2] = useState<
    Maybe<string> | undefined
  >(user?.address?.addressComplementary2);
  const [zipCode, setZipCode] = useState<Maybe<string> | undefined>(
    user?.address?.zipCode
  );
  const [city, setCity] = useState<Maybe<string> | undefined>(
    user?.address?.city
  );
  const [phone1, setPhone1] = useState<Maybe<string> | undefined>(
    user?.address?.phone1
  );
  const [phone2, setPhone2] = useState<Maybe<string> | undefined>(
    user?.address?.phone2
  );

  /* Password Change Mode State */
  const [isChangePasswordMode, setIsChangePasswordMode] =
    useState<boolean>(false);

  /* Customer additionnal détails */
  const [customerCriteriaValueInput, setCustomerCriteriaValueInput] = useState<
    CriteriaValueInput[]
  >([]);

  useEffect(() => {
    if (user) {
      setEmail(user?.address?.mail);
      setUsername(user.login);
      setLastname(user.lastName ?? "");
      setFirstname(user.firstName ?? "");
      setLanguage(user.serviceLanguage?.code);
      setCivility(user.civility?.code);
      setCountry(user.address?.addressCountry?.code);
      setStreetName(user.address?.streetName);
      setPostalLocality(user.address?.postalLocality);
      setAddressComplementary1(user.address?.addressComplementary1);
      setAddressComplementary2(user.address?.addressComplementary2);
      setZipCode(user.address?.zipCode);
      setCity(user.address?.city);
      setPhone1(user.address?.phone1);
      setPhone2(user.address?.phone2);
      setBirthdate(user?.birthDate ? moment(new Date(user.birthDate)) : null);
    }
  }, [user]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (
      (firstname && firstname.trim().length <= 1) ||
      (lastname && lastname.trim().length <= 1)
    ) {
      toast.error("LANG_neobooking_register_invalid_lastname_firstname");
      return;
    }

    setIsSubmitting(true);
    const toastEdit = toast.loading(
      `${getLanguageVarValue(
        "LANG_neobooking_updating_account_in_progress"
      )}...`
    );
    const _customerCriteriaValueInput: CriteriaValueInput[] = Object.entries(
      customerCriteriaValueInput
    ).map(([key, val]) => {
      return {
        code: key,
        value: val,
      };
    }) as any;

    update(
      username!!,
      null,
      null,
      civility!!,
      firstname!!,
      lastname!!,
      birthdate ? birthdate.toDate() : new Date(),
      language!!,
      streetName!!,
      postalLocality!!,
      addressComplementary1!!,
      addressComplementary2!!,
      zipCode!!,
      city!!,
      country!!,
      phone1!!,
      phone2!!,
      email!!,
      _customerCriteriaValueInput,
      (err) => {
        if (err) {
          toast.error(err.message, {
            id: toastEdit,
          });
          console.log(err);
        } else {
          toast.success(
            getLanguageVarValue("LANG_neobooking_account_successfully_updated"),
            {
              id: toastEdit,
            }
          );
        }
        setIsSubmitting(false);
      }
    );
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className="tw-my-6">
          <div className="tw-my-4">
            <div className="tw-grid tw-grid-cols-12 tw-gap-6">
              <div className="tw-col-span-12">
                <div className="tw-block sm:tw-hidden">
                  <SelectCivility
                    required
                    value={civility!!}
                    onChange={(e) => setCivility(e.target.value)}
                    additionalClassname="tw-w-full"
                  />
                </div>
                <div className="tw-hidden sm:tw-block">
                  <div className="tw-mb-2">
                    {getLanguageVarValue("LANG_neobooking_civility")}*
                  </div>
                  <RadioCivility
                    required
                    value={civility!!}
                    onChange={(e) => setCivility(e.target.value)}
                  />
                </div>
              </div>
              <div className="tw-col-span-12 md:tw-col-span-6">
                <InputText
                  editIcon
                  required
                  placeholder={getLanguageVarValue("LANG_neobooking_lastname")}
                  value={lastname!!}
                  onChange={(e) => setLastname(e.target.value)}
                  additionalClassname="tw-w-full"
                  type="text"
                />
              </div>
              <div className="tw-col-span-12 md:tw-col-span-6">
                <InputText
                  editIcon
                  required
                  placeholder={getLanguageVarValue("LANG_neobooking_firstname")}
                  value={firstname!!}
                  onChange={(e) => setFirstname(e.target.value)}
                  additionalClassname="tw-w-full"
                  type="text"
                />
              </div>
              <div className="tw-col-span-12 md:tw-col-span-6">
                <SDPicker
                  selectedSingleDate={birthdate}
                  setSelectedSingleDate={setBirthdate}
                />
              </div>
              <div className="tw-col-span-12 md:tw-col-span-6">
                <InputText
                  editIcon
                  placeholder={getLanguageVarValue("LANG_neobooking_adr_tel_1")}
                  value={phone1!!}
                  onChange={(e) => setPhone1(e.target.value)}
                  additionalClassname="tw-w-full"
                  type="text"
                />
              </div>
              <div className="tw-col-span-12">
                <InputText
                  editIcon
                  required
                  value={streetName!!}
                  onChange={(e) => setStreetName(e.target.value)}
                  placeholder={getLanguageVarValue(
                    "LANG_neobooking_adr_street"
                  )}
                  additionalClassname="tw-w-full"
                  type="text"
                />
              </div>
              <div className="tw-col-span-12">
                <div className="tw-grid tw-grid-cols-12 tw-gap-4">
                  <div className="tw-col-span-6">
                    <InputText
                      editIcon
                      required
                      placeholder={getLanguageVarValue(
                        "LANG_neobooking_adr_zipcode"
                      )}
                      value={zipCode!!}
                      onChange={(e) => setZipCode(e.target.value)}
                      additionalClassname="tw-w-full"
                      type="text"
                    />
                  </div>
                  <div className="tw-col-span-6">
                    <InputText
                      editIcon
                      required
                      placeholder={getLanguageVarValue("LANG_neobooking_city")}
                      value={city!!}
                      onChange={(e) => setCity(e.target.value)}
                      additionalClassname="tw-w-full"
                      type="text"
                    />
                  </div>
                </div>
              </div>
              <div className="tw-col-span-12">
                <InputText
                  editIcon
                  placeholder={getLanguageVarValue(
                    "LANG_neobooking_email_address"
                  )}
                  value={email!!}
                  onChange={(e) => setEmail(e.target.value)}
                  additionalClassname="tw-w-full"
                  type="text"
                />
              </div>
            </div>
          </div>
          <div className="tw-my-4">
            <CustomerCriterias
              criteriaCodes={["NEWS", "SMS"]}
              customerCriteriaValueInput={customerCriteriaValueInput}
              setCustomerCriteriaValueInput={setCustomerCriteriaValueInput}
            />
          </div>
          <Button
            type="submit"
            variant="gradient"
            loadingChildren={
              <>{getLanguageVarValue("LANG_neobooking_editing")}...</>
            }
            disabled={
              password !== passwordConfirm ||
              (isEmpty(password) && isEmpty(passwordConfirm)) ||
              isSubmitting
            }
            isLoading={isSubmitting}
          >
            {getLanguageVarValue("LANG_neobooking_confirm_my_information")}
          </Button>
        </div>
      </form>
    </>
  );
};

export const CustomerPassword: React.FC = () => {
  /* Hooks */
  const { sessionId, getLanguageVarValue, authMode } = useSession();
  const client = useApolloClient();
  const { user, update, setUser } = useAuth();

  /* States */
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [isSubmittingPassword, setIsSubmittingPassword] =
    useState<boolean>(false);

  const handleSubmitChangePassword = (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmittingPassword(true);
    const toastEdit = toast.loading(
      `${getLanguageVarValue(
        "LANG_neobooking_updating_account_in_progress"
      )}...`
    );

    if (authMode === 2) {
      //B2B, on fait un updatePartnerAccount
      client
        .mutate<Mutation, MutationUpdatePartnerAccountArgs>({
          mutation: updatePartnerAccountMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              account: {
                password: password,
                password2: confirmPassword,
              },
              address: {
                mail: user?.address?.mail ?? "",
              },
              birthDate:
                user?.birthDate ??
                dateFormat(moment().add(-30, "years").toDate(), "yyyy-mm-dd"),
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (
            response &&
            response.data &&
            response.data.updatePartnerAccount &&
            response.data.updatePartnerAccount.service
          ) {
            toast.success(
              getLanguageVarValue(
                "LANG_neobooking_account_successfully_updated"
              ),
              {
                id: toastEdit,
              }
            );
            setUser(response.data.updatePartnerAccount.service);
          }
        })
        .catch((err) => {
          toast.error(err.message, {
            id: toastEdit,
          });
        })
        .finally(() => {
          setIsSubmittingPassword(false);
        });
    } else {
      update(
        user?.login ?? "",
        password,
        confirmPassword,
        user?.civility?.code ?? "",
        user?.firstName ?? "",
        user?.lastName ?? "",
        user?.birthDate,
        user?.serviceLanguage?.code ?? "",
        user?.address?.streetName ?? "",
        user?.address?.postalLocality ?? "",
        user?.address?.addressComplementary1 ?? "",
        user?.address?.addressComplementary2 ?? "",
        user?.address?.zipCode ?? "",
        user?.address?.city ?? "",
        user?.address?.addressCountry?.code ?? "",
        user?.address?.phone1 ?? "",
        user?.address?.phone2 ?? "",
        user?.address?.mail ?? "",
        [],
        (err) => {
          if (err) {
            toast.error(err.message, {
              id: toastEdit,
            });
            console.log(err);
          } else {
            toast.success(
              getLanguageVarValue(
                "LANG_neobooking_account_successfully_updated"
              ),
              {
                id: toastEdit,
              }
            );
          }
          setIsSubmittingPassword(false);
        }
      );
    }
  };

  return (
    <div className="tw-mt-6">
      <div className="tw-text-grey-500 tw-font-medium tw-mb-1">
        <div>{getLanguageVarValue("LANG_neobooking_password")}</div>
      </div>
      <form onSubmit={handleSubmitChangePassword}>
        <div className="tw-flex tw-flex-col tw-gap-6">
          <div className="tw-flex tw-flex-col tw-gap-y-6">
            <InputPassword
              minLength={passwordMinLength}
              showStrength={true}
              value={password}
              required
              onChange={(e) => setPassword(e.target.value)}
              additionalClassname="tw-w-full"
              placeholder={getLanguageVarValue("LANG_neobooking_new_password")}
            />
            <InputPasswordConfirm
              required
              onChange={(e) => setConfirmPassword(e.target.value)}
              value={confirmPassword}
              passwordToCheck={password}
              showConfirmation={true}
              additionalClassname="tw-w-full"
              placeholder={getLanguageVarValue(
                "LANG_neobooking_password_confirm"
              )}
            />
          </div>
        </div>
        <div className="tw-mt-8">
          <Button
            disabled={
              isSubmittingPassword ||
              (isEmpty(password) && isEmpty(confirmPassword)) ||
              password !== confirmPassword
            }
            isLoading={isSubmittingPassword}
            type="submit"
            variant="gradient"
          >
            {getLanguageVarValue("LANG_neobooking_confirm")}
          </Button>
        </div>
      </form>
    </div>
  );
};
type CustomerDetailsBoxProps = {
  user: Service | null;
  detailsExpand?: boolean;
  setDetailsExpand?: (value: boolean) => void;
  showSignout?: boolean;
};

export const CustomerDetailsBox: React.FC<CustomerDetailsBoxProps> = ({
  user,
  showSignout = false,
  detailsExpand,
  setDetailsExpand,
}) => {
  /* Hooks */
  const { getLanguageVarValue, authMode } = useSession();
  const { signout } = useAuth();

  /* States */
  const [loadingSignout, setLoadingSignout] = useState<boolean>(false);
  const [expanded, setDetailsExpanded] = useState<boolean>(
    detailsExpand ?? false
  );

  /* Functions */

  const handleExpand = (value: boolean) => {
    if (setDetailsExpand) {
      setDetailsExpand(value);
    }
    setDetailsExpanded(value);
  };

  const handleSignout = () => {
    setLoadingSignout(true);
    const toastSignout = toast.loading(
      getLanguageVarValue("LANG_neobooking_logout_in_progress")
    );
    signout((err) => {
      if (err) {
        toast.error(err.message, {
          id: toastSignout,
        });
      } else {
        toast.success(
          getLanguageVarValue("LANG_neobooking_logout_successfully"),
          {
            id: toastSignout,
          }
        );
      }
      setLoadingSignout(false);
    });
  };

  /* Render */
  return (
    <div className="tw-col-span-2 lg:tw-col-span-1 tw-mt-6">
      <div className="tw-flex tw-flex-col tw-gap-y-4">
        <div className="tw-flex tw-flex-col">
          <div className="tw-text-grey-500 tw-font-medium">
            {getLanguageVarValue("LANG_neobooking_email")}
          </div>
          <div className="tw-text-grey-900">{user?.address?.mail}</div>
        </div>
        <div className="tw-flex tw-flex-col tw-text-grey-900">
          <div className="tw-font-medium">
            {user?.civility?.label} {user?.firstName} {user?.lastName}
          </div>
          {authMode !== 2 && user?.birthDate && (
            <div>{dateFormat(new Date(user?.birthDate), "mm/dd/yyyy")}</div>
          )}
          <div>
            {user?.address?.streetName} / {user?.address?.zipCode}{" "}
            {user?.address?.city} / {user?.address?.addressCountry?.label}
          </div>
          <div>
            {user?.address?.phone1}
            {user?.address?.phone2
              ? " / " + user?.address?.phone2
              : user?.address?.fax
              ? " / " + user.address.fax
              : ""}
          </div>
        </div>
        {showSignout && (
          <div className="tw-text-left">
            <span className="tw-text-grey-900 tw-font-medium">
              {getLanguageVarValue("LANG_neobooking_isnt_you") + " "}
            </span>
            <Button
              type="button"
              onClick={handleSignout}
              isLoading={loadingSignout}
              disabled={loadingSignout}
              shape="link"
              additionalClassname="!tw-font-semibold !tw-underline !tw-tracking-normal"
            >
              {getLanguageVarValue("LANG_neobooking_sign_out")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

CustomerDetails.propTypes = {
  //
};

export default CustomerDetails;
